import { Location } from 'history';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import NavigationBar from 'sharedreact/components/NavigationBar';
import NavigationBarItem from 'sharedreact/components/NavigationBarItem';
import NavigationBarLayout from 'sharedreact/components/NavigationBarLayout';
import NavigationBarLink from 'sharedreact/components/NavigationBarLink';
import NavigationBarMenu from 'sharedreact/components/NavigationBarMenu';
import Root from 'sharedreact/components/Root';
import Spinner from 'sharedreact/components/Spinner';
import SubAppRedirect from 'sharedreact/components/SubAppRedirect';
import AboutContainer from 'sharedreact/containers/AboutContainer';
import AccountContainer from 'sharedreact/containers/AccountContainer';
import AppLauncherContainer from 'sharedreact/containers/AppLauncherContainer';
import ChangePasswordDialogContainer from 'sharedreact/containers/ChangePasswordDialogContainer';
import ErrorContainer from 'sharedreact/containers/ErrorContainer';
import PageNotFound404ErrorContainer from 'sharedreact/containers/PageNotFound404ErrorContainer';
import SessionWarningContainer from 'sharedreact/containers/SessionWarningContainer';
import SettingsContainer from 'sharedreact/containers/SettingsContainer';
import User from 'sharedreact/data/User';
import { translate } from 'sharedreact/utils/LanguageSupport';

import AdminContainer from 'workspaces/launcher/containers/admin/AdminContainer';
import AuthenticationLauncherContainer from 'workspaces/launcher/containers/authentication/AuthenticationLauncherContainer';
import ClientAppsContainer from 'workspaces/launcher/containers/clientapps/ClientAppsContainer';
import WebAppsLauncherContainer from 'workspaces/launcher/containers/webappslauncher/WebAppsLauncherContainer';
import LocationState from 'sharedreact/utils/LocationState';

import __BUILD_VERSION__ from 'workspaces/launcher/utils/BuildVersion';

const quickstartRedirectPaths: string[] = [
  '/createpress',
  '/createproducts',
  '/customers',
  '/customer',
  '/dies',
  '/die',
  '/marksets',
  '/ordermaterials',
  '/ordermaterial',
  '/presses',
  '/press',
  '/products',
  '/product',
  '/productfileinfo',
  '/productioncart',
  '/productionjobs',
  '/productionjob',
  '/productionloginfo',
  '/settings',
  '/system',
  '/todos',
  '/todo',
  '/users',
  '/user',
  '/viewer',
  '/productionjobtemplates',
  '/productionjobtemplate',
  '/selectjobtemplate'
];

type Props = {
  language: string,
  theme: string,
  font: string,
  location: Location<LocationState>,
  isLoggedIn: boolean,
  activeUser: User,
  attemptRelogin: boolean,
  hasWorkspaces: boolean,
  userSettingsLoaded: boolean,

  toggleSettings: () => void,
  toggleAccount: () => void,
  onLoad?: () => void,
  onUnload?: () => void,
  onShowELearning: () => void,
  onShowDocumentation: () => void,
  onShowAbout: () => void,
  onShowAppLauncher: () => void,
};

class App extends React.Component<Props> {
  override componentDidMount (): void {
    if (this.props.onLoad != null) {
      this.props.onLoad();
    }
  }

  override componentWillUnmount (): void {
    if (this.props.onUnload != null) {
      this.props.onUnload();
    }
  }

  _renderView () {
    const { language, // eslint-disable-line no-unused-vars
      location, isLoggedIn, attemptRelogin, hasWorkspaces, activeUser
    } = this.props;

    if (!isLoggedIn) {
      if (attemptRelogin) {
        return <Spinner />;
      } else if (location.pathname !== '/login') {
        return <Redirect to={{pathname: '/login', state: {referrer: location}}} />;
      }
    }

    if (isLoggedIn && location.pathname === '/login' && (location.state == null || !location.state.logout)) {
      if (location.state != null && location.state.referrer != null) {
        return <Redirect to={location.state.referrer} />;
      } else {
        return <Redirect to='/' />;
      }
    } else if (!isLoggedIn && location.state != null && location.state.logout) {
      return <Redirect to={{pathname: '/login', state: {}}} />;
    }

    const redirectToSubPath = (redirectTo: string) => {
      return () => <SubAppRedirect subAppPath={redirectTo} />;
    };

    const isAdminUser = (activeUser && activeUser.role === 'admin');

    return (
      <Switch>
        {quickstartRedirectPaths.map((path) => {
          return <Route key={path} path={path} component={redirectToSubPath('/labels')} />;
        })}
        <Route exact path='/' render={
            () => <Redirect to={hasWorkspaces ? '/workspaces' : '/downloads'} />
            } />
        <Route path='/login'
          component={AuthenticationLauncherContainer} />
        {hasWorkspaces
          ? <Route path='/workspaces'
            component={WebAppsLauncherContainer} />
          : null
        }
        <Route path='/downloads'
          component={ClientAppsContainer} />
        {isAdminUser
          ? <Route path='/admin'
            component={AdminContainer} />
          : null
        }
        <Route path='/login' component={AuthenticationLauncherContainer} />
        <Route component={PageNotFound404ErrorContainer} />
      </Switch>
    );
  }

  _renderNavBar () {
    const { location, isLoggedIn, userSettingsLoaded,
      activeUser, toggleSettings, toggleAccount,
      onShowELearning, onShowDocumentation, onShowAbout, onShowAppLauncher, hasWorkspaces } = this.props;

    const appName: string = 'Automation Engine';
    const appIcon: string = 'static/media/aeicon32.png?v=' + __BUILD_VERSION__;

    const logout: boolean = (location.state != null && (location.state.logout ?? false));

    const isRedirect: boolean = quickstartRedirectPaths.includes(location.pathname);

    if (!isLoggedIn || !userSettingsLoaded || logout || isRedirect) {
      return (
        <NavigationBar
          appName={appName}
          appIcon={appIcon} />
      );
    }

    const isAdminUser = (activeUser && activeUser.role === 'admin');

    return (
      <NavigationBar
        appName={appName}
        appIcon={appIcon}
        onBrandClick={onShowAppLauncher} >
        <NavigationBarLayout>
          {hasWorkspaces
            ? <NavigationBarLink id='ae_menu_workspaces'
              to='/workspaces'>
              {translate('launcher.clientapps.workspaces')}
            </NavigationBarLink>
            : null
          }
          <NavigationBarLink id='ae_menu_downloads'
            to='/downloads'>
            {translate('launcher.clientapps.title')}
          </NavigationBarLink>
          {isAdminUser
            ? <NavigationBarLink id='ae_menu_admin'
              to='/admin'>
              {translate('launcher.admin.title')}
            </NavigationBarLink>
            : null
          }
        </NavigationBarLayout>
        <NavigationBarLayout alignRight>
          <NavigationBarMenu key='helpmenu'
            id='ae_menu_help'
            title={''}
            icon='esko_icon esko_icon-help'
            collapse='sm'
          >
            <NavigationBarItem key='elearning'
              id='ae_menu_elearning'
              onClick={onShowELearning}
              allowLargeText
            >
              {translate('launcher.nav.elearning')}
            </NavigationBarItem>
            <NavigationBarItem key='documentation'
              id='ae_menu_documentation'
              onClick={onShowDocumentation}
              allowLargeText
            >
              {translate('launcher.nav.documentation')}
            </NavigationBarItem>
            <li className='esko_divider' />
            <NavigationBarItem key='about'
              id='ae_menu_about'
              onClick={onShowAbout}
              allowLargeText
            >
              {translate('launcher.nav.about')}
            </NavigationBarItem>
          </NavigationBarMenu>
          <NavigationBarMenu key='logonmenu'
            id='ae_menu_logon'
            title={(activeUser != null ? activeUser.name : '')}
            icon='bi bi-person-fill'
            collapse='sm'
            allowLargeText
            originalText>
            <NavigationBarItem id='ae_menu_settings' onClick={toggleSettings} >
              {translate('launcher.nav.settings')}
            </NavigationBarItem>
            <NavigationBarItem id='ae_menu_account' onClick={toggleAccount} >
              {translate('launcher.nav.account')}
            </NavigationBarItem>
            <NavigationBarLink key='logOut'
              id='ae_menu_logout'
              to={{pathname: '/login', state: {logout: true, referrer: location}}}
              allowLargeText>
              {translate('launcher.nav.signout')}
            </NavigationBarLink>
          </NavigationBarMenu>
        </NavigationBarLayout>
      </NavigationBar>
    );
  }

  override render (): React.ReactNode {
    // only render the normal page, when user settings are loaded
    const _doRender = this.props.language !== '' && (!this.props.isLoggedIn || this.props.userSettingsLoaded);

    return (
      <Root theme={this.props.theme} font={this.props.font}>
        {this._renderNavBar()}
        {_doRender ? <SessionWarningContainer /> : null}
        {_doRender ? <ErrorContainer language={this.props.language} /> : null}
        {_doRender ? <AppLauncherContainer /> : null}
        {_doRender ? <SettingsContainer showUnits={false} /> : null}
        {_doRender ? <AccountContainer /> : null}
        {_doRender ? <AboutContainer /> : null }
        {_doRender ? <ChangePasswordDialogContainer /> : null}
        {_doRender
         ? this._renderView()
         : <Spinner />
        }
      </Root>
    );
  }
}

export default App;
